// OneDrive:IgnoreCodeCoverage
import IDisposable from '../disposable/IDisposable';
import EventGroup from '../events/EventGroup';
import Scope from '../scope/Scope';

export interface IBreakpoints {
    readonly Small: number;
    readonly Medium: number;
    readonly Large: number;
    readonly XLarge: number;
    readonly XXLarge: number;
    readonly XXXLarge: number;
    // This value should be Infinity, but it is not a valid literal type at present.
    readonly XXXXLarge: typeof Infinity;
}

/**
 * Enum that describes the current window size
 */
export type WindowSize = keyof IBreakpoints;

export interface IResponsiveUIParams {
    // Nothing added
}

export interface IResponsiveUIDependencies {
    breakpoints: IBreakpoints | (() => IBreakpoints);
}

/**
 * Event names fired when ResponsiveUI properties change
 */
export const ResponsiveUIEvents = {
    // TODO make this a const enum
    formFactorChange: 'formFactorChange',
    hamburgerVisibilityChange: 'hamburgerVisibilityChange',
    showLabelsChange: 'showCommandLabelsChange'
};

// These values are matched up with Fabric's LESS definitions
const cssClassFromWindowSize: { readonly [K in WindowSize]: string } = {
    Small: 'sm',
    Medium: 'md',
    Large: 'lg',
    XLarge: 'xlg',
    XXLarge: 'xxlg',
    XXXLarge: 'xxxlg',
    XXXXLarge: 'xxxxlg'
};

const defaultBreakpoints: IBreakpoints = {
    Small: 480,
    Medium: 640,
    Large: 1024,
    XLarge: 1366,
    XXLarge: 1920,
    XXXLarge: 2560,
    XXXXLarge: Infinity
};

export class ResponsiveUI implements IDisposable {
    public static readonly DEFAULT_BREAKPOINTS: IBreakpoints = defaultBreakpoints;

    /** The width of the hidden scrollbars in Mac */
    public static readonly MAC_SCROLL_BAR_WIDTH: number = 8;

    protected _scope: Scope;
    protected _events: EventGroup;

    private _breakpoints: () => IBreakpoints;
    private _windowWidth: number;
    private _windowHeight: number;
    private _formFactorSize: WindowSize;
    private _isHamburgerVisible: boolean;
    private _cssClass: string;
    private _showLabels: boolean;

    constructor(params: IResponsiveUIParams, dependencies: IResponsiveUIDependencies) {
        this._scope = new Scope();
        this._events = new (this._scope.attached(EventGroup))(this);

        this._events.on(window, 'resize, orientationchange', this._onContainerResize.bind(this));

        let breakpoints = dependencies.breakpoints;
        this._breakpoints = typeof breakpoints === 'function' ? breakpoints : () => <IBreakpoints>breakpoints;
        this._windowWidth = 0;
        this._windowHeight = 0;

        this._onContainerResize();
    }

    public static reduceBreakpoints(breakpoints: IBreakpoints, newSmall: number): IBreakpoints {
        return {
            Small: newSmall,
            Medium: breakpoints.Small,
            Large: breakpoints.Medium,
            XLarge: breakpoints.Large,
            XXLarge: breakpoints.XLarge,
            XXXLarge: breakpoints.XXLarge,
            XXXXLarge: Infinity
        };
    }

    public static determineWindowSize(elementWidth: number, breakpoints: IBreakpoints = defaultBreakpoints): WindowSize {
        if (elementWidth === 0) {
            return 'Medium';
        }

        // Using defaultBreakpoints as the key space to enforce ordering.
        for (const key in defaultBreakpoints) {
            if (elementWidth < breakpoints[key]) {
                return key as WindowSize;
            }
        }
    }

    public static determineCssClass(formFactor: WindowSize) {
        return cssClassFromWindowSize[formFactor];
    }

    /** Calculates the width of the scroll bar of the given browser/OS */
    public static getScrollbarWidth(): number {
        const outer = document.createElement('div');
        const inner = document.createElement('div');

        const outerStyle = outer.style;
        outerStyle.visibility = 'hidden';
        outerStyle.width = '100px';
        outerStyle.msOverflowStyle = 'scrollbar';
        outerStyle.overflow = 'scroll';
        inner.style.width = '100%';

        document.body.appendChild(outer);
        outer.appendChild(inner);

        const widthNoScroll = outer.offsetWidth;
        const widthWithScroll = inner.offsetWidth;

        outer.parentNode.removeChild(outer);

        const scrollbarWidth = widthNoScroll - widthWithScroll;

        this.getScrollbarWidth = () => scrollbarWidth;

        return scrollbarWidth;
    }

    /** Current width of the window */
    public get windowWidth(): number {
        return this._windowWidth;
    }

    /** Current height of the window */
    public get windowHeight(): number {
        return this._windowHeight;
    }

    /** Current window size */
    public get formFactorSize(): WindowSize {
        return this._formFactorSize;
    }

    /** Indicates whether a hamburger should be visible or not */
    public get isHamburgerVisible(): boolean {
        return this._isHamburgerVisible;
    }

    /** Whether or not to show command labels at the current form factor when using LabelVisibility.responsive */
    public get showLabels(): boolean {
        return this._showLabels;
    }

    /** A css class that can be used anywhere for responsive css classes */
    public get cssClass(): string {
        return this._cssClass;
    }

    public dispose(): void {
        this._scope.dispose();
    }

    /**
     * Compares two {WindowSize} values.
     * @returns positive if a > b, negative if a < b, 0 if a === b.
     */
    public compareSize(a: WindowSize, b: WindowSize): number {
        const breakpoints = this._breakpoints();

        return breakpoints[a] - breakpoints[b];
    }

    /**
     * Method child classes can use for custom post-resize logic (such as observable updates).
     */
    protected _afterContainerResize() {
        // For child classes only
    }

    /**
     * Method called when the container resizes.
     * For custom logic, prefer overriding _afterContainerResize rather than this method.
     */
    protected _onContainerResize() {
        const {
            body,
            documentElement
        } = document;

        const windowWidth = this._windowHeight = documentElement.clientWidth || body.clientWidth;
        this._windowWidth = documentElement.clientHeight || body.clientHeight;

        const oldFormFactorSize = this._formFactorSize;
        const formFactorSize = this._formFactorSize = ResponsiveUI.determineWindowSize(windowWidth, this._breakpoints());
        this._cssClass = ResponsiveUI.determineCssClass(formFactorSize);

        // This is determined by the suite nav, so it always uses default breakpoints.
        const oldIsHamburgerVisible = this._isHamburgerVisible;
        this._isHamburgerVisible = windowWidth < defaultBreakpoints.Large;

        const oldShowLabels = this._showLabels;
        this._showLabels = this.compareSize(formFactorSize, 'Medium') > 0;

        // After updating state, raise events if the values have changed.
        if (oldFormFactorSize && formFactorSize !== oldFormFactorSize) {
            this._events.raise(ResponsiveUIEvents.formFactorChange);
        }

        if (typeof oldIsHamburgerVisible === 'boolean' && oldIsHamburgerVisible !== this._isHamburgerVisible) {
            this._events.raise(ResponsiveUIEvents.hamburgerVisibilityChange);
        }

        if (typeof oldShowLabels === 'boolean' && oldShowLabels !== this._showLabels) {
            this._events.raise(ResponsiveUIEvents.showLabelsChange);
        }

        this._afterContainerResize();
    }
}

export default ResponsiveUI;
